import type { BrandStyle } from '~/design-system/components/DeckGen/types/presentation'

/**
 * SCREEN TRUTH for a scraped brand palette — re-rank and filter the backend's CSS-derived BrandStyle
 * against the page's own SCREENSHOT, which is the only artefact that knows what the site actually paints.
 *
 * WHY THIS EXISTS
 * ---------------
 * `BrandStyleExtractionService` (backend) ranks colours by FREQUENCY OF DECLARATION IN THE CSS TEXT — a
 * property-context pass over the stylesheet, plus a +100 score for any custom property whose NAME contains
 * accent/primary/brand. It never rasterises the page. A framework stylesheet declares every colour it
 * ships, so DEAD UTILITY COLOURS OUT-VOTE THE ONES ON SCREEN.
 *
 * The case that forced this (Guri, x.ai): x.ai is a black/white/grey site. The extracted palette came back
 * led by a teal #156e7a, followed by #3b82f6 and #22c55e — literally Tailwind's default blue-500 and
 * green-500, shipped in the bundle and painted nowhere. Generation then produced teal decks for a
 * monochrome brand.
 *
 * The fix is entirely client-side (no backend deploy): the web card already holds `screenshotUrl`, the
 * rendered page. We sample it, build a colour histogram, and ask of every scraped colour: DOES THE PAGE
 * ACTUALLY PAINT THIS, AND HOW MUCH? Colours the render never shows are dropped; the rest are ordered by
 * the area they occupy, which is what "most prominent first" was always supposed to mean.
 *
 * NEVER REGRESS. Every failure path — no screenshot, tainted canvas, decode error, zero buckets, a
 * histogram that corroborates nothing — returns the ORIGINAL BrandStyle by identity. This layer can only
 * make a palette more truthful or leave it exactly as it was. It never invents a colour: replacements are
 * always drawn from colours the SITE DECLARED, never from a raw screenshot bucket (a full-bleed hero photo
 * would otherwise hand us a stock-photo colour as the brand).
 *
 * The BrandStyle shape is unchanged, so `brandStyleToTokens` (generation) and `buildBrandStyleTiles`
 * (swatch row) consume the corrected style without knowing this pass exists.
 *
 * FILE LAYOUT: everything above `--- canvas sampling ---` is PURE and unit-tested
 * (`__tests__/birtvyBrandTruth.test.ts`); everything below touches the DOM.
 */

// --- tuning constants ------------------------------------------------------------------------------
// Every number here is calibrated against measured OKLab distances, not picked round. The table that
// justifies them is in the comments at each constant.

/** Total pixels sampled from the screenshot. 65 536 = a 256×256-equivalent census: enough that the 0.1%
 *  area floor is ~65 hits (≈8σ over Poisson noise), cheap enough to run inline on the main thread. */
export const SAMPLE_TARGET_PIXELS = 65_536

/** Sample only the TOP 6 000px of a full-page screenshot. THIS IS THE ONE DELIBERATE BIAS IN THE RANKER,
 *  and it is load-bearing: a full-page shot can be 20 000px tall, and the bottom is footers, legal text
 *  and link lists. Letting that into the denominator dilutes a real accent below any sane floor. The top
 *  ~5 viewports are where a brand is expressed, and they are also what the card shows at rest. */
export const SAMPLE_MAX_SOURCE_HEIGHT = 6_000

/** 5 bits per channel → 32 768 buckets of 8 code values each. Buckets report their MEAN, not their
 *  centre, so the quantisation step barely affects the ranking (see `sampleScreenColors`). */
export const QUANT_BITS = 5

/**
 * Match radius in OKLab (Euclidean ΔEok). MEASURED calibration:
 *   #156e7a ↔ #71717a (the x.ai phantom vs mid grey)   0.096   ← must NOT match: 2.4× τ
 *   #3b82f6 ↔ #71717a                                   0.190
 *   #3b82f6 ↔ #2563eb (adjacent ramp step)              0.082   ← must NOT match: blue-600 pixels may
 *                                                                  never "confirm" blue-500
 *   #22c55e ↔ #16a34a                                   0.098
 *   #f4f4f5 ↔ #e4e4e7 (neutral ramp step)               0.048
 *   #ffffff ↔ #fafafa                                   0.015   ← folds, correctly
 *   #3b82f6 ↔ its 10%-toward-white composite            0.040   ← must match: opacity / hover tint / AA
 * So 0.040 absorbs ~10% compositing drift while sitting below half the spacing of adjacent design-ramp
 * steps. Plain RGB Euclidean CANNOT separate the x.ai case (teal and mid grey are ~90/441 apart in RGB,
 * inside any tolerance wide enough to absorb antialiasing) — that is why this is OKLab and not RGB.
 */
export const MATCH_TAU = 0.040

/** Palette membership floor: 0.1% of sampled pixels. Five times below the bottom of the 0.5–3% band a
 *  genuine accent occupies, ~8σ above Poisson noise at our sample size, and semantically right — a colour
 *  covering under 1 pixel in 1000 contributes nothing to how the brand looks. */
export const PALETTE_AREA_FLOOR = 0.001

/** ROLE PRESENCE floor: "did this colour appear AT ALL". Deliberately 3.3× lower than the palette floor,
 *  and COLLAPSING THE TWO REINTRODUCES THE BUG IN THE OTHER DIRECTION: a 1px border stroke across a whole
 *  page is ~0.05% of area, and body text at 14px is mostly antialiased blends with only stroke cores
 *  landing on the true hex. Judging `border`/`text` by a PROMINENCE floor would delete structurally
 *  required roles. 0.001 gates palette membership; 0.0003 gates role presence. */
export const ROLE_PRESENCE_FLOOR = 0.0003

/**
 * Neutral cut on OKLab chroma = hypot(a, b). MEASURED: pure greys 0.000 · #f4f4f5 0.001 · #18181b 0.006 ·
 * #71717a 0.014 · #eff6ff 0.014 · #111827 0.032 · #0f172a 0.040 · #64748b 0.041 (all tinted neutrals) vs
 * #3b82f6 0.188 · #22c55e 0.192 · #ea580c 0.194 · #5b21b6 0.211 · #e11d48 0.222 (all real accents). The
 * gap between 0.041 and 0.188 is wide and empty; 0.050 sits in it. No lightness clause — a deep navy
 * background SHOULD class as neutral. (The x.ai phantom teal is 0.080, i.e. non-neutral: it dies from
 * ABSENCE, not from chroma. That is the correct reason for it to die.)
 */
export const NEUTRAL_CHROMA_MAX = 0.050

/** Corroboration gate: unless SOME scraped colour (palette or role) covers ≥5% of the render, the
 *  screenshot and the stylesheet are not describing the same page — a cookie wall, a consent interstitial,
 *  a 404, a hero video frame. Trust nothing and hand the original style straight back. */
export const CORROBORATION_MIN_MASS = 0.05

/** A background is the largest painted region on a page by definition. If the CSS background is absent
 *  from the render we only replace it with a DECLARED neutral that covers at least this much of the
 *  render — anything less is not a background, and guessing one wrong inverts every contrast decision
 *  downstream. */
export const BACKGROUND_OVERRIDE_MIN_MASS = 0.10

// --- pure core -------------------------------------------------------------------------------------

/** One quantised colour observed in the render, with the fraction of sampled pixels it covers. */
export interface ScreenColorBucket {
  /** The bucket's MEAN colour as #rrggbb (exact for a flat fill — see `sampleScreenColors`). */
  hex: string
  /** count / sampled pixels, in [0, 1]. */
  area: number
}

export interface Oklab {
  L: number
  a: number
  b: number
}

const HEX6 = /^#?([0-9a-f]{6})$/i
const HEX3 = /^#?([0-9a-f]{3})$/i

/** `#abc` / `#aabbcc` → [r, g, b]. Anything else (a named colour, `rgba(...)`, junk) → null; the caller
 *  KEEPS such an entry rather than judging it, because an unreadable colour is not evidence of absence. */
export function parseHex(hex: string): [number, number, number] | null {
  if (typeof hex !== 'string') return null
  const six = HEX6.exec(hex.trim())
  if (six) {
    const n = parseInt(six[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const three = HEX3.exec(hex.trim())
  if (three) {
    const c = three[1]
    return [parseInt(c[0] + c[0], 16), parseInt(c[1] + c[1], 16), parseInt(c[2] + c[2], 16)]
  }
  return null
}

export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
  return `#${((1 << 24) | (clamp(r) << 16) | (clamp(g) << 8) | clamp(b)).toString(16).slice(1)}`
}

const srgbToLinear = (c: number): number => {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

/** sRGB → OKLab (Björn Ottosson's matrices). Three cbrt calls, no white-point plumbing — CIELab would
 *  separate these colours equally well and cost an illuminant argument nobody would maintain. */
export function rgbToOklab(r: number, g: number, b: number): Oklab {
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  }
}

export function oklabOfHex(hex: string): Oklab | null {
  const rgb = parseHex(hex)
  return rgb ? rgbToOklab(rgb[0], rgb[1], rgb[2]) : null
}

/** Euclidean ΔEok. */
export function deltaEok(x: Oklab, y: Oklab): number {
  return Math.hypot(x.L - y.L, x.a - y.a, x.b - y.b)
}

/** OKLab chroma — how far the colour is from the grey axis. */
export function chromaOf(c: Oklab): number {
  return Math.hypot(c.a, c.b)
}

/** A grey, a near-grey, or a tinted neutral (off-white, navy, slate). See NEUTRAL_CHROMA_MAX. */
export function isNeutralHex(hex: string): boolean {
  const lab = oklabOfHex(hex)
  return lab ? chromaOf(lab) < NEUTRAL_CHROMA_MAX : false
}

interface PreparedBucket {
  lab: Oklab
  area: number
}

function prepareBuckets(buckets: readonly ScreenColorBucket[]): PreparedBucket[] {
  const out: PreparedBucket[] = []
  for (const bucket of buckets) {
    if (!bucket || !(bucket.area > 0)) continue
    const lab = oklabOfHex(bucket.hex)
    if (lab) out.push({ lab, area: bucket.area })
  }
  return out
}

/**
 * MATCHED MASS: the total render area within MATCH_TAU of `hex`.
 *
 * Deliberately a RADIUS SUM, not a nearest-bucket lookup. A flat fill under a gradient overlay, a hover
 * tint, or a compression-jittered region fragments across neighbouring buckets; summing the neighbourhood
 * reassembles it and makes the quantisation step nearly irrelevant to the ranking. Nearest-bucket would
 * score the same colour differently depending on where the 8-step grid happened to fall.
 */
export function matchedMass(hex: string, buckets: readonly ScreenColorBucket[]): number {
  const lab = oklabOfHex(hex)
  if (!lab) return 0
  return massOfLab(lab, prepareBuckets(buckets))
}

function massOfLab(lab: Oklab, prepared: readonly PreparedBucket[]): number {
  let mass = 0
  for (const bucket of prepared) {
    if (deltaEok(lab, bucket.lab) <= MATCH_TAU) mass += bucket.area
  }
  return mass
}

/** What the re-rank decided, for logging and tests. */
export interface BrandTruthReport {
  style: BrandStyle
  changed: boolean
  /** Palette entries the render never paints (or paints below the floor). */
  dropped: string[]
  /** Matched mass per input palette entry, keyed by the hex as it was given. */
  mass: Record<string, number>
  /** Set when a role's colour failed the presence floor and was replaced. */
  replacedRoles: Record<string, { from: string; to: string }>
  /** Why nothing happened, when nothing happened. */
  bailed?: 'no-buckets' | 'no-corroboration' | 'no-survivors'
}

/**
 * THE PURE CORE. Re-rank + filter a scraped BrandStyle against a screenshot histogram.
 *
 *  • palette → entries whose matched mass ≥ PALETTE_AREA_FLOOR, ordered by mass descending. Unparseable
 *    entries are kept, untouched, after the ranked ones (absence of evidence, not evidence of absence).
 *  • roles.accent → kept if it is present at all (ROLE_PRESENCE_FLOOR). Otherwise the highest-mass
 *    NON-NEUTRAL survivor; otherwise `roles.text`, mirroring the backend's own monochrome fallback
 *    (BrandStyleExtractionService.classifyRoles). Never a raw bucket.
 *  • roles.background → replaced only when it is absent from the render AND a declared neutral covers
 *    ≥ BACKGROUND_OVERRIDE_MIN_MASS.
 *  • every other role is left exactly as the backend classified it: they are structural (text/border must
 *    contrast with the background), they are judged at the presence floor by design, and no replacement
 *    exists that is safe for contrast. The bug being fixed is a phantom ACCENT, not a phantom border.
 *
 * Returns the INPUT OBJECT BY IDENTITY whenever it decides not to act, so callers can cheaply test
 * `result.style === style`.
 */
export function rerankBrandStyleByScreen(style: BrandStyle, buckets: readonly ScreenColorBucket[]): BrandTruthReport {
  const unchanged = (bailed?: BrandTruthReport['bailed']): BrandTruthReport => ({
    style,
    changed: false,
    dropped: [],
    mass: {},
    replacedRoles: {},
    bailed,
  })
  if (!style) return unchanged('no-buckets')

  const prepared = prepareBuckets(buckets ?? [])
  if (prepared.length === 0) return unchanged('no-buckets')

  const palette = Array.isArray(style.palette) ? style.palette : []
  const roles: Record<string, string> = style.roles ?? {}

  const massCache = new Map<string, number>()
  const massOf = (hex: string): number => {
    const key = hex.toLowerCase()
    const hit = massCache.get(key)
    if (hit !== undefined) return hit
    const lab = oklabOfHex(hex)
    const value = lab ? massOfLab(lab, prepared) : 0
    massCache.set(key, value)
    return value
  }

  // Corroboration: does this histogram describe the page the stylesheet came from at all?
  let bestKnown = 0
  for (const hex of [...palette, ...Object.values(roles)]) {
    if (typeof hex === 'string' && parseHex(hex)) bestKnown = Math.max(bestKnown, massOf(hex))
  }
  if (bestKnown < CORROBORATION_MIN_MASS) return unchanged('no-corroboration')

  const entries = palette.map((hex, index) => ({
    hex,
    index,
    readable: typeof hex === 'string' && parseHex(hex) !== null,
    mass: typeof hex === 'string' ? massOf(hex) : 0,
  }))
  const keptUnreadable = entries.filter((e) => !e.readable)
  const survivors = entries
    .filter((e) => e.readable && e.mass >= PALETTE_AREA_FLOOR)
    // Ties keep the backend's original order — its CSS ranking is a reasonable secondary signal.
    .sort((a, b) => b.mass - a.mass || a.index - b.index)

  // A palette that survives nothing means the sampling and the CSS disagree completely. Never hand the
  // canvas an empty swatch row.
  if (survivors.length === 0) return unchanged('no-survivors')

  const nextPalette = [...survivors.map((e) => e.hex), ...keptUnreadable.map((e) => e.hex)]
  const dropped = entries.filter((e) => e.readable && e.mass < PALETTE_AREA_FLOOR).map((e) => e.hex)

  const nextRoles: Record<string, string> = { ...roles }
  const replacedRoles: BrandTruthReport['replacedRoles'] = {}

  const accent = roles.accent
  if (typeof accent === 'string' && parseHex(accent) && massOf(accent) < ROLE_PRESENCE_FLOOR) {
    const declaredAccent = survivors.find((e) => !isNeutralHex(e.hex))
    const replacement = declaredAccent?.hex ?? (typeof roles.text === 'string' ? roles.text : undefined)
    if (replacement && replacement.toLowerCase() !== accent.toLowerCase()) {
      nextRoles.accent = replacement
      replacedRoles.accent = { from: accent, to: replacement }
    }
  }

  const background = roles.background
  if (typeof background === 'string' && parseHex(background) && massOf(background) < ROLE_PRESENCE_FLOOR) {
    const replacement = survivors.find((e) => isNeutralHex(e.hex) && e.mass >= BACKGROUND_OVERRIDE_MIN_MASS)
    if (replacement && replacement.hex.toLowerCase() !== background.toLowerCase()) {
      nextRoles.background = replacement.hex
      replacedRoles.background = { from: background, to: replacement.hex }
    }
  }

  const paletteMoved =
    nextPalette.length !== palette.length || nextPalette.some((hex, i) => hex !== palette[i])
  const rolesMoved = Object.keys(replacedRoles).length > 0
  if (!paletteMoved && !rolesMoved) {
    return {
      style,
      changed: false,
      dropped: [],
      mass: Object.fromEntries(entries.map((e) => [e.hex, e.mass])),
      replacedRoles: {},
    }
  }

  return {
    style: { ...style, palette: nextPalette, roles: nextRoles },
    changed: true,
    dropped,
    mass: Object.fromEntries(entries.map((e) => [e.hex, e.mass])),
    replacedRoles,
  }
}

// --- canvas sampling -------------------------------------------------------------------------------
// Everything below touches the DOM. Kept in this file so the whole "screen truth" idea reads in one
// place, but strictly separated from the pure core above and never invoked at import time.

/** Per-URL memo — a screenshot never changes behind its URL (a re-scrape mints a new hash), so one sample
 *  serves the tile row, the generation path and any re-run. `null` records a permanent failure so a
 *  tainted or broken image is not re-decoded on every call. */
const bucketCache = new Map<string, ScreenColorBucket[] | null>()

/** Ceiling on a single screenshot decode. Without it a stalled same-origin response that never fires
 *  `onerror` leaves the promise pending forever — and because the ingest path stashes the corrected style
 *  only AFTER this resolves, the card would end up with NO brandStyle at all. Losing the palette is worse
 *  than keeping an imperfect one, so a timeout rejects and the caller falls back to the scraped style. */
const DECODE_TIMEOUT_MS = 8000

function decodeImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const timer = window.setTimeout(() => reject(new Error('screenshot decode timed out')), DECODE_TIMEOUT_MS)
    const settle = (fn: () => void) => {
      window.clearTimeout(timer)
      fn()
    }
    // NO crossOrigin, ON PURPOSE. `screenshotUrl` is a RELATIVE, same-origin path
    // (`/api/v1/storage/figma-images/<hash>.png` — StoragePathConfig), served permitAll from our own
    // origin, and BirtvyWebCard renders it as a bare <img> with no crossOrigin. Setting the attribute
    // would buy no taint protection we do not already have, and it forks the HTTP cache entry — the
    // browser would download the multi-MB page shot a SECOND time instead of reusing the card's.
    img.decoding = 'async'
    img.onload = () => settle(() => resolve(img))
    img.onerror = () => settle(() => reject(new Error('screenshot decode failed')))
    img.src = src
    if (typeof img.decode === 'function') {
      img.decode().then(() => settle(() => resolve(img))).catch(() => {
        /* onload / onerror still settle this promise */
      })
    }
  })
}

/**
 * Screenshot → colour histogram. Returns [] on ANY failure (no DOM, decode error, tainted canvas, empty
 * image) — callers treat [] as "no evidence" and keep the original palette.
 *
 * TWO CHOICES THAT MATTER:
 *
 *  1. `imageSmoothingEnabled = false`. Bilinear downscale of a 1280×6000 page into ~120px INVENTS colours:
 *     an accent button smeared with its white surround produces a pale intermediate that exists nowhere
 *     on the page. Nearest-neighbour makes every destination pixel a REAL source pixel, so the histogram
 *     is an unbiased point-sample census. Bilinear is for display; nearest is for counting.
 *
 *  2. Buckets report their MEAN, not the fixed bucket centre. This is a deliberate departure from
 *     "centre = (q<<3)|4", and it is not cosmetic: OKLab's lightness is a cube root, so near black the
 *     perceptual step per code value is enormous — ΔEok(#000000, #040404) = 0.107, i.e. 2.7× MATCH_TAU.
 *     With fixed centres a PURE BLACK BACKGROUND WOULD MISS ITS OWN BUCKET and be dropped as absent,
 *     which is exactly the x.ai case this file exists for. The mean is exact for a flat fill (the shots
 *     are lossless PNG), and for a mixed bucket it is still a real convex combination of real pixels
 *     inside the same 8-step box, so it is never worse than the centre.
 *
 * Cost: one ~65k pixel loop plus ≤32k OKLab conversions downstream, ≈5–15ms, once per screenshot, after a
 * network fetch has already completed. If it ever shows up in a profile, this function is self-contained
 * enough to move to an OffscreenCanvas worker without touching the pure core.
 */
export async function sampleScreenColors(screenshotUrl: string): Promise<ScreenColorBucket[]> {
  if (!screenshotUrl) return []
  const cached = bucketCache.get(screenshotUrl)
  if (cached !== undefined) return cached ?? []
  if (typeof document === 'undefined') return []
  try {
    const img = await decodeImage(screenshotUrl)
    const naturalW = img.naturalWidth
    const naturalH = img.naturalHeight
    if (!naturalW || !naturalH) throw new Error('empty decode')

    const srcW = naturalW
    const srcH = Math.min(naturalH, SAMPLE_MAX_SOURCE_HEIGHT) // top of the page only — see the constant
    // Clamped at 1: a shot smaller than the target needs no upscale (nearest-neighbour would just
    // duplicate pixels, changing nothing but the cost).
    const scale = Math.min(1, Math.sqrt(SAMPLE_TARGET_PIXELS / (srcW * srcH)))
    const sw = Math.max(32, Math.round(srcW * scale))
    const sh = Math.max(32, Math.round(srcH * scale))

    const canvas = document.createElement('canvas')
    canvas.width = sw
    canvas.height = sh
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    ctx.imageSmoothingEnabled = false
    // Opaque underlay, so a transparent PNG cannot contribute alpha-weighted garbage and the denominator
    // is simply sw*sh (mirrors moodboardStyle's drawToJpegDataUrl).
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, sw, sh)
    ctx.drawImage(img, 0, 0, srcW, srcH, 0, 0, sw, sh)

    // Taint probe + the only read. Same-origin means this should never throw; it is the belt anyway,
    // because a throw here is the difference between "keep the original palette" and a broken card.
    const { data } = ctx.getImageData(0, 0, sw, sh)

    const counts = new Map<number, number>()
    const sums = new Map<number, [number, number, number]>()
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3)
      const prev = counts.get(key)
      if (prev === undefined) {
        counts.set(key, 1)
        sums.set(key, [r, g, b])
      } else {
        counts.set(key, prev + 1)
        const acc = sums.get(key)!
        acc[0] += r
        acc[1] += g
        acc[2] += b
      }
    }

    const total = sw * sh
    const out: ScreenColorBucket[] = []
    for (const [key, count] of counts) {
      if (count < 2) continue // the singleton tail is antialias noise, not colour
      const acc = sums.get(key)!
      out.push({ hex: rgbToHex(acc[0] / count, acc[1] / count, acc[2] / count), area: count / total })
    }
    out.sort((a, b) => b.area - a.area)
    bucketCache.set(screenshotUrl, out)
    return out
  } catch {
    bucketCache.set(screenshotUrl, null)
    return []
  }
}

/**
 * The one call sites use: a scraped BrandStyle + the card's screenshot → the same style with the palette
 * re-ranked against what the page actually paints. Returns the input by identity on every failure path.
 */
export async function brandStyleFromScreen(style: BrandStyle, screenshotUrl?: string): Promise<BrandStyle> {
  if (!style || !screenshotUrl) return style
  try {
    const buckets = await sampleScreenColors(screenshotUrl)
    if (buckets.length === 0) return style
    const report = rerankBrandStyleByScreen(style, buckets)
    if (report.changed && import.meta.env?.DEV) {
      // One line, only when the render actually contradicted the CSS — this is the signal that tells us
      // whether the phantom-colour class of bug is still happening in the wild.
      console.info(
        '[brand-truth] screenshot re-ranked palette',
        { palette: report.style.palette, dropped: report.dropped, roles: report.replacedRoles },
      )
    }
    return report.style
  } catch {
    return style
  }
}

/** Test seam — drops the memo so a suite (or a re-scrape of the same URL in dev) can sample again. */
export function clearScreenColorCache(): void {
  bucketCache.clear()
}
